// src/components/portal/MandateHeader.js — the shared mandate title block:
// standard CRM-style title, then GP firm and structure/sector, right-aligned,
// on the right side of the page header row.
//
// The left side of that same row is one of two things, never both: the GP
// logo (GP_LOGOS, keyed by mandate_name) on the mandate's own overview page,
// or — on a derivative page that passes `title`/`description` — that
// page's own subtitle in the logo's place instead. Showing the logo AND a
// derivative page's subtitle at once read as mixed in with the mandate
// name; this keeps the row to one thing on the left, always centered on
// the y axis against the whole row (`alignSelf: 'center'`).
//
// `back` is optional — a { to, label } pair rendered as its own row above
// everything else, flush left against the true page edge, and NOT a flex
// sibling of the logo/header row below it — putting it inside that row is
// what pushed it rightward past the logo the first time this was tried.
// This is the portal-wide standard for where a back link sits; any future
// page that needs one should follow the same placement rather than
// inventing its own.
//
// `showSector` defaults to true for PortalMandateDetail (the mandate's own
// page); derivative pages branching off a mandate (PortalAddNames, and any
// future one) pass `showSector={false}` to show just name + GP firm.
import { Link } from 'react-router-dom'
import { GP_LOGOS, GP_LOGOS_LIGHT_OVERRIDE } from '../../lib/gpLogos'
import { PORTAL_PAGE_TITLE_STYLE, PORTAL_TITLE_ALIGN } from '../../lib/portalPageTitleStyle'
import { usePortalTheme } from '../../context/PortalThemeContext'

export default function MandateHeader({ mandate, back, showSector = true, title, description }) {
  const { theme } = usePortalTheme()
  const logo = (theme === 'light' && GP_LOGOS_LIGHT_OVERRIDE[mandate.mandate_name])
    || GP_LOGOS[mandate.mandate_name]
  const hasSubtitle = Boolean(title || description)

  return (
    <div>
      {back && (
        <p style={{ marginBottom: 16, color: 'var(--text-primary)' }}><Link to={back.to}>&larr; {back.label}</Link></p>
      )}
      {/* Left-aligned titles reverse the row so the mandate name leads and
          the logo (or Add Names' title block) sits on the right. */}
      <div className="page-header" style={PORTAL_TITLE_ALIGN === 'left' ? { flexDirection: 'row-reverse' } : undefined}>
        {hasSubtitle ? (
          <div style={{ flex: 1, minWidth: 0, alignSelf: 'center', textAlign: PORTAL_TITLE_ALIGN === 'left' ? 'right' : 'left' }}>
            {title && (
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: 'var(--text-primary)' }}>{title}</h2>
            )}
            {description && (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{description}</p>
            )}
          </div>
        ) : (
          <>
            {logo && (
              <img
                src={logo}
                alt=""
                style={{ alignSelf: 'center', flexShrink: 0, maxWidth: 140, maxHeight: 64, objectFit: 'contain' }}
              />
            )}
            {/* Empty spacer — keeps the row at two flex children (this +
                the mandate-name column) so `justify-content: space-between`
                still pushes the mandate name flush right even with no
                logo. */}
            <div style={{ flex: 1, minWidth: 0 }} />
          </>
        )}
        <div className="page-header-text" style={{ flexShrink: 0 }}>
          <h1 style={PORTAL_PAGE_TITLE_STYLE}>{mandate.mandate_name}</h1>
          <p style={{ textAlign: PORTAL_TITLE_ALIGN, fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', marginTop: 6 }}>
            {mandate.gp_firm_name ?? '—'}
          </p>
          {showSector && mandate.structure_sector && (
            <p style={{ textAlign: PORTAL_TITLE_ALIGN, fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              {mandate.structure_sector}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
